/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React, { FC } from 'react';
import clsx from 'clsx';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import ChartDataLabels, { Context } from 'chartjs-plugin-datalabels';
import { useIntl } from 'react-intl';

import { CHART_COLORS } from '@futrli/colors';
// import { formatCurrency, formatDecimal, WrappedComponentProps } from '@futrli/localisation';

import styles from './donut-chart.scss';
import { Font } from 'chartjs-plugin-datalabels/types/options';

interface Props {
  ariaLabel?: string;
  legend?: 'top' | 'bottom' | 'left' | 'right';
  title?: string;
  labelFormatter?: (value: number, context: { dataIndex: number }) => string | number;
  labels: (string | string[])[];
  datasets: { label?: string; data: number[]; colors?: string | string[]; circumference?: number }[];
  testId?: string;
  graphOptions?: Record<string, unknown>;
  hideDataLabels?: boolean;
}

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);
ChartJS.defaults.color = '#7C879D';
ChartJS.defaults.font = {
  family: 'SageUI',
  size: 10,
  weight: '500'
};
ChartJS.defaults.plugins.tooltip.backgroundColor = 'rgb(92, 101, 120)';

export enum POSITIONS {
  START = 'start',
  END = 'end',
  CENTER = 'center'
}

export const UIDonutChart: FC<Props> = ({
  ariaLabel,
  legend,
  title,
  labels,
  datasets,
  testId,
  graphOptions,
  hideDataLabels,
  labelFormatter = Math.round
}: Props) => {
  const intl = useIntl();
  const options = {
    responsive: true,
    devicePixelRatio: 4,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: legend ? 15 : 25,
        right: legend ? 10 : 70,
        bottom: legend ? 0 : 25,
        left: legend ? 10 : 50
      }
    },
    plugins: {
      legend: {
        display: !!legend,
        position: legend || 'top',
        align: graphOptions?.alignLegend as 'start' | 'end' | 'center' | undefined
      },
      title: {
        display: !!title,
        text: title || ''
      },
      datalabels: hideDataLabels
        ? { display: false }
        : {
            color: '#7C879D',
            display: (ctx: Context) => Math.abs((ctx.dataset.data[ctx.dataIndex] as number) || 0) > 0,
            font: {
              weight: 'bold'
            } as Font,
            padding: 2,
            formatter: labelFormatter
          }
    },
    ...(graphOptions ? graphOptions : {})
  };

  const pieData = {
    labels: labels.map(l => (typeof l === 'string' && l.length > 30 ? `${l.substring(0, 27)}...` : l)),
    datasets: datasets.map(ds => ({
      label: ds.label || '',
      data: ds.data,
      backgroundColor: ds.colors || CHART_COLORS.POSITIVE.PRIMARY,
      rotation: ds.circumference ? 0 : -90,
      circumference: ds.circumference || 180,
      datalabels: {
        anchor: POSITIONS.END,
        align: POSITIONS.END,
        offset: 0
      }
    }))
  };

  return (
    <div className={clsx(styles.chartWrapper)} data-testid={testId || 'doughnut-chart'}>
      <Doughnut
        data={pieData}
        options={options}
        aria-label={
          ariaLabel ||
          intl.formatMessage({
            id: 'predict.chart.donut_chart.label',
            defaultMessage: 'Donut chart'
          })
        }
      />
    </div>
  );
};
